import { useRef,useState } from 'react';
import { useMutation,useQuery } from '@tanstack/react-query';
import api from '@/lib/api';

export type MetaCostScope={storeId:string|null;accountId:string;periodFrom:string;periodUntil:string;level:'ACCOUNT'|'CAMPAIGNS';campaignIds:string[]};
type Snapshot={id:string;ordinal:number;recordedAt:string;contentHash:string};
type History={knowledgeAsOf:string;snapshot:Snapshot|null;previousSnapshot:Snapshot|null;
  source:{account:{reportedCurrency:string};observations:{insightId:string;campaignId:string|null;date:string;reportedSpend:string}[]}|null;
  report:{knownObservedSpend:string|null;missing:{campaignId:string|null;missingDays:number}[]}|null;
  changes:{accountChanged:boolean;added:string[];removed:string[];changed:string[]}|null};
export function useFinanceMetaCostArchive(tenantId:string|undefined,scope:MetaCostScope|null,observedHash:string|undefined){
  const [cutoff,setCutoff]=useState(''),[pin,setPin]=useState<string|null>(null),[page,setPage]=useState(0);
  const [requestedScope,setRequestedScope]=useState<string|null>(null);
  const scopeKey=JSON.stringify([tenantId,scope]);
  const nonce=useRef<{key:string;value:string}|null>(null);
  const options={enabled:false,retry:false,refetchOnWindowFocus:false,refetchOnReconnect:false,gcTime:0} as const;
  const history=useQuery({...options,enabled:requestedScope===scopeKey,queryKey:['finance-meta-archive',scopeKey,cutoff,pin],queryFn:async()=>{
    if(!scope)throw new Error('Wybierz konto, okres i kampanie.');
    const knowledgeAsOf=cutoff?new Date(cutoff).toISOString():undefined;
    return (await api.get<History>(`/finance/facebook/accounts/${encodeURIComponent(scope.accountId)}/cost-source-archive`,{
      params:{...scope,storeId:scope.storeId??undefined,campaignIds:scope.campaignIds.length?scope.campaignIds.join(','):undefined,
        snapshotId:pin??undefined,knowledgeAsOf}})).data;
  }});
  const archive=useMutation({retry:false,mutationFn:async(command:{scope:MetaCostScope;scopeKey:string;expectedContentHash:string;acquisitionKey:string})=>{
    const {scope:args,...rest}=command;
    const response=await api.post<{snapshot:Snapshot;replayed:boolean}>(`/finance/facebook/accounts/${encodeURIComponent(args.accountId)}/cost-source-archive`,
      {...args,expectedContentHash:rest.expectedContentHash,acquisitionKey:rest.acquisitionKey});
    return {...response.data,scopeKey:command.scopeKey};
  }});
  return {history,archive,cutoff,pin,page,setPage,canArchive:!!scope&&!!observedHash&&!cutoff&&!pin,
    saved:archive.data?.scopeKey===scopeKey?archive.data:undefined,
    busy:history.isFetching||archive.isPending,
    save:()=>{
      if(!scope||!observedHash||cutoff||pin)return;
      const key=JSON.stringify([scopeKey,observedHash]);if(nonce.current?.key!==key)nonce.current={key,value:crypto.randomUUID()};
      archive.mutate({scope,scopeKey,expectedContentHash:observedHash,acquisitionKey:nonce.current.value});
    },
    loadHistory:()=>{if(requestedScope===scopeKey)void history.refetch();else setRequestedScope(scopeKey);},
    reset:()=>{setRequestedScope(null);setCutoff('');setPin(null);setPage(0);archive.reset();nonce.current=null;},
    setCutoff:(value:string)=>{setRequestedScope(null);setCutoff(value);setPin(null);setPage(0);},
    openPrevious:()=>{if(history.data?.previousSnapshot){setPin(history.data.previousSnapshot.id);setPage(0);}},
    latest:()=>{setPin(null);setPage(0);},
  };
}
